import {
    createVideoFlight,
    deleteVideoFlight,
    updateVideoFlight,
} from "features/pubsub/entityActions/flights/video"
import { ModalNames } from "features/modals/types"
import { PluginRegistry, PluginSpec } from "plugins/plugin-registry"
import * as Icons from "@nova/component-icons"
import { LiveVideoFlight, VideoFlight } from "@nova/protobuf"
import {
    LiveVideoStatus,
    selectLiveVideoStatusById,
    selectVideoFlightById,
    selectVideoFlights,
} from "features/entity-caching/flights/selectors"
import { zoomToVideo } from "features/map/inspector-panel/videos/module"
import { SidePanelLayerType } from "@nova/constants"
import { getGenericLayerDataInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { InspectorPanelVideoLayer } from "features/map/inspector-panel/videos/InspectorPanelVideoLayer"
import { InspectorPanelVideoData } from "features/map/inspector-panel/videos/InspectorPanelVideoData"
import { getGenericUploadFlowActiveFeatureSidePanelComponent } from "panels/content/side-panel/active-feature-components/GenericActiveFeatureSidePanelComponent"
import { VideoMultifileUpload } from "./VideoMultifileUpload"
import { useVideoStatusAndProgress } from "panels/content/side-panel/progress/Video"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { VideoAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/VideoAssetsThumbnail"
import {
    getGenericLayerIconComponent,
    getGenericLayerListItem,
} from "panels/content/side-panel/generics/GenericLayerListItem"
import { getUseVideoListItemActions } from "panels/content/side-panel/generics/genericListItemActions"
import { getUseVideoFlightPath } from "../shared/useVideoFlightPath"
import { useAppSelector } from "integrations/redux/hooks"
import { selectIsVideoFlightOpen } from "../state/selectors"
import { selectFollowingEntityId } from "plugins/shared/playback/state/selectors"
import { useIsPlayingVideo } from "../controllers/hooks/useControllerEvents"
import { getUseVideoMapNodes } from "features/map/map-nodes/useVideoMapNodes"

export function registerVideoPlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "video",
            displayName: "Video",
            sidePanelLayerType: SidePanelLayerType.VideoFlight,
            PrimaryIcon: Icons.VideoPlugin,
            ImageIconUrl: "/images/plugins/layers/video.svg",
            updateAction: updateVideoFlight,
            deleteAction: deleteVideoFlight,
            confirmDelete: () => true,
            protobufMessage: VideoFlight,
            selectAllEntities: selectVideoFlights,
            selectSingleEntity: selectVideoFlightById,
            useUploadStatusAndProgress: useVideoStatusAndProgress,
            zoomTo: zoomToVideo,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            SidePanelListItem: getGenericLayerListItem({
                spec,
                useActions: getUseVideoListItemActions(spec),
                LayerIconComponent: getVideoLayerIconComponent(spec),
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: VideoAssetsThumbnail,
            }),
        }))
        .implement(
            "side-panel-active-feature-plugin",
            () =>
                ({
                    SidePanelComponent: getGenericUploadFlowActiveFeatureSidePanelComponent({
                        MultifileUploadComponent: VideoMultifileUpload,
                    }),
                }) as const
        )
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericLayerDataInspectorPanelComponent({
                spec,
                LayerComponent: InspectorPanelVideoLayer,
                DataComponent: InspectorPanelVideoData,
                isScrollable: true,
            }),
        }))
        .implement("feature-plugin", () => ({
            createAction: createVideoFlight,
            sampleDataModalName: ModalNames.TryVideoModal,
        }))
        .implement("map-layer-plugin", (spec) => ({
            useLayer: getUseVideoFlightPath(spec, { allowCrosshair: true, allowFrame: true }),
        }))
        .implement("map-node-plugin", (spec) => ({
            useMapNodes: getUseVideoMapNodes(spec),
        }))
}

export function getVideoLayerIconComponent(
    spec: PluginSpec<VideoFlight> | PluginSpec<LiveVideoFlight>
) {
    const GenericLayerIconComponent = getGenericLayerIconComponent(spec)
    const LiveVideoLayerIconComponent = ({ id }: { id: string }) => {
        const isActive = useAppSelector(selectIsVideoFlightOpen(id))
        const isPlaying = useIsPlayingVideo({ flightId: id })
        const isFollowing = useAppSelector(selectFollowingEntityId) === id
        const liveVideoStatus = useAppSelector(selectLiveVideoStatusById(id))

        const iconVariant = (() => {
            if (liveVideoStatus === LiveVideoStatus.Standby) return Icons.DroneVideoIcon
            if (isActive && !isPlaying) return Icons.PauseCircle
            if (isFollowing) return Icons.AssistantNavigationFilled
            if (isActive) return Icons.PlayCircle
        })()

        if (iconVariant) {
            return (
                <div className="w-5 h-5 flex justify-center items-center">
                    <Icons.ExtraSmallIcon
                        className="shrink-0"
                        Icon={iconVariant}
                        iconClassName="text-blue-500"
                    />
                </div>
            )
        }
        return <GenericLayerIconComponent id={id} />
    }
    return LiveVideoLayerIconComponent
}
